import React, { useMemo, useState } from 'react';
import {
  FileText,
  Loader2,
  AlertTriangle,
  Info,
  FilePlus2,
  ArrowLeft,
  Save,
  Trash2,
  Plus,
} from 'lucide-react';
import {
  ARKO_CONTRACT_GENERATE_URL,
  getPortalTenantN8nExtras,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  resolveOnlyofficeIssueJwtUrl,
} from '../../constants';
import type {
  ContractSchema,
  ContractTemplateDef,
  ContractValues,
  SchemaProp,
} from './contractTemplates.ts';
import {
  CONTRACT_TEMPLATES,
  INHALTE_ROW_TONE_CLASS,
  buildEngineSubmitFields,
  buildInitialFormValues,
  defaultEnabledBlockIds,
  getByPath,
  getContractTemplate,
  inhalteRowTone,
  isBlockActive,
  isConditionalActive,
  isEffectivelyRequired,
  collectLeafFields,
  isEngineTemplate,
  nextInhalteHoverId,
  schemaForEnabledBlocks,
  setByPath,
  validateContractForm,
} from './contractTemplates.ts';
import type { EigeneKlausel, SavedContractDraft } from './contractDraftStore.ts';
import {
  deleteContractDraft,
  loadContractDrafts,
  MAX_INDIVIDUELL_BLOCKS,
  newIndividuellBlock,
  normalizeIndividuellBlocks,
  saveContractDraft,
} from './contractDraftStore.ts';
import {
  GESELLSCHAFTER_LIST_KEY,
  GESELLSCHAFTER_MIN_COUNT,
  PHILLIP_MUSTER_LABEL,
  emptyGesellschafterEintrag,
  gesellschafterLetter,
  isGesellschafterSchemaKey,
  resolveGesellschafterListe,
  syncGesellschafterForm,
  type GesellschafterListeEintrag,
} from './contractGeruest.ts';
import { EMPTY_SLOT, buildGeruestBlockPreview, buildGeruestPreview } from './contractGeruestPreview.ts';
import { PORTAL_BTN } from '../arkorbit-ui/portalTheme';

interface ContractGeneratorViewProps {
  customerId: string;
  customerStatus?: string;
  customerEmail?: string;
  /** S4 Akt-Hub: explizites Ziel; der aufrufende Hub bestätigt die PB-Aktbindung. */
  targetAkteId?: string;
  targetFolder?: string;
  /** Antwort des Generate-Endpoints (wie PORTAL_DOCUMENT_UPLOAD) → öffnet den Editor. */
  onDocumentReady: (data: Record<string, unknown>, res: Response) => void | Promise<void>;
  /** Vom ARKO-Chip: Typ schon gewählt, direkt in diesen Ersteller. Leer = Typ-Auswahl wie bisher. */
  initialTemplateId?: string | null;
}

/** Schwarzer Grund, feine lila Kante — wie die Akte. Keine weiße Karte, keine blaue Leiste. */
const VERTRAG_GROUND = 'bg-[#07070a]';
const VERTRAG_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
const VERTRAG_SHELL =
  `flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-y-auto ${VERTRAG_GROUND} ${VERTRAG_EDGE}`;
const FIELD_BASE =
  'w-full bg-[#07070a] border border-violet-400/30 rounded-md px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-violet-400 transition-colors placeholder-slate-600';

const ContractGeneratorView: React.FC<ContractGeneratorViewProps> = ({
  customerId,
  customerStatus,
  customerEmail,
  targetAkteId,
  targetFolder,
  onDocumentReady,
  initialTemplateId,
}) => {
  const seededId =
    initialTemplateId && getContractTemplate(initialTemplateId) ? initialTemplateId : '';
  const [pickerOpen, setPickerOpen] = useState(() => !seededId);
  const [templateId, setTemplateId] = useState<string>(
    seededId || CONTRACT_TEMPLATES[0]?.id || '',
  );
  const template = getContractTemplate(templateId);

  const [form, setForm] = useState<ContractValues>(() =>
    template ? buildInitialFormValues(template.schema) : {},
  );
  const [enabledBlocks, setEnabledBlocks] = useState<string[]>(() =>
    template ? defaultEnabledBlockIds(template) : [],
  );
  const [extraValues, setExtraValues] = useState<Record<string, unknown>>({});
  const [eigeneKlauseln, setEigeneKlauseln] = useState<EigeneKlausel[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftNotice, setDraftNotice] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<SavedContractDraft[]>(() => loadContractDrafts(customerId));
  const [loadId, setLoadId] = useState('');

  const schema = useMemo(
    () => (template ? schemaForEnabledBlocks(template, enabledBlocks) : undefined),
    [template, enabledBlocks],
  );

  const leafByPath = useMemo(() => {
    const map: Record<string, ReturnType<typeof collectLeafFields>[number]> = {};
    if (schema) for (const f of collectLeafFields(schema)) map[f.path] = f;
    return map;
  }, [schema]);

  const applyTemplate = (tpl: ContractTemplateDef) => {
    setTemplateId(tpl.id);
    setForm(buildInitialFormValues(tpl.schema));
    setEnabledBlocks(defaultEnabledBlockIds(tpl));
    setExtraValues({});
    setEigeneKlauseln([]);
    setErrors({});
    setServerError(null);
    setDraftNotice(null);
    setDraftName('');
    setLoadId('');
    setPickerOpen(false);
  };

  const update = (path: string, value: unknown) => {
    setForm((prev) => setByPath(prev, path, value));
    setErrors((prev) => {
      if (!prev[path]) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const toggleBlock = (blockId: string) => {
    if (!template) return;
    const block = template.blocks.find((b) => b.id === blockId);
    if (!block || block.locked) return;
    const on = !isBlockActive(template, enabledBlocks, blockId);
    setEnabledBlocks((prev) => (on ? [...prev, blockId] : prev.filter((id) => id !== blockId)));
    if (block.bindsBoolean) {
      update(block.bindsBoolean, on);
    }
  };

  const handleSaveDraft = () => {
    if (!template) return;
    try {
      const id =
        loadId && drafts.some((d) => d.id === loadId)
          ? loadId
          : typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `t-${Date.now()}`;
      const saved = saveContractDraft(customerId, {
        id,
        name: draftName.trim() || `${template.label} — Entwurf`,
        templateId,
        form,
        enabledBlocks,
        extraValues,
        eigeneKlauseln,
      });
      setDrafts(loadContractDrafts(customerId));
      setLoadId(saved.id);
      setDraftName(saved.name);
      setDraftNotice('Vorlage in diesem Browser gespeichert — nur auf diesem Gerät und diesem Konto.');
    } catch (err) {
      setServerError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleLoadDraft = (id: string) => {
    const found = drafts.find((d) => d.id === id);
    if (!found) return;
    const tpl = getContractTemplate(found.templateId);
    if (!tpl) {
      setServerError('Dieser Vertragstyp ist in diesem Build nicht mehr verfügbar.');
      return;
    }
    setTemplateId(tpl.id);
    setForm({ ...buildInitialFormValues(tpl.schema), ...found.form });
    setEnabledBlocks(found.enabledBlocks?.length ? found.enabledBlocks : defaultEnabledBlockIds(tpl));
    setExtraValues(found.extraValues || {});
    setEigeneKlauseln(normalizeIndividuellBlocks(found.eigeneKlauseln));
    setDraftName(found.name);
    setLoadId(found.id);
    setPickerOpen(false);
    setErrors({});
    setServerError(null);
    setDraftNotice(`Vorlage „${found.name}“ geladen.`);
  };

  const handleDeleteDraft = () => {
    if (!loadId) return;
    deleteContractDraft(customerId, loadId);
    setDrafts(loadContractDrafts(customerId));
    setLoadId('');
    setDraftNotice('Vorlage gelöscht (nur auf diesem Gerät).');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schema || !template) return;
    setServerError(null);

    if (!isEngineTemplate(template.id)) {
      const preview = buildGeruestPreview(template, form, enabledBlocks, extraValues, eigeneKlauseln);
      const copy =
        typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function'
          ? navigator.clipboard.writeText(preview)
          : Promise.reject(new Error('no-clipboard'));
      copy
        .then(() => {
          setDraftNotice('Entwurf in die Zwischenablage kopiert (nur auf diesem Gerät, kein ARKORBIT-AGB).');
          setServerError(null);
        })
        .catch(() => {
          setServerError(
            'Zwischenablage nicht verfügbar — bitte den Fließtext unten markieren. Es wird keine Datei auf dem Server erzeugt.',
          );
        });
      return;
    }

    const validation = validateContractForm(schema, form);
    if (!validation.ok) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});

    if (!ARKO_CONTRACT_GENERATE_URL) {
      setServerError('Der Vertragsgenerator ist in diesem Build nicht konfiguriert.');
      return;
    }
    if (!hasPortalAuthzToken()) {
      setServerError('Bitte neu anmelden — für den Vertragsgenerator fehlt ein gültiges Portal-Token.');
      return;
    }

    const fields = buildEngineSubmitFields(template, form, enabledBlocks, eigeneKlauseln);
    const displayName = buildDisplayName(template.label, form);

    setBusy(true);
    try {
      const url = resolveOnlyofficeIssueJwtUrl(ARKO_CONTRACT_GENERATE_URL);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...portalAuthzHeaders(url),
        },
        credentials: 'omit',
        body: JSON.stringify({
          templateType: templateId,
          fields,
          customerId,
          display_name: displayName,
          ...(targetAkteId?.trim() ? { akte_id: targetAkteId.trim() } : {}),
          ...(targetFolder?.trim() ? { folderPath: targetFolder.trim() } : {}),
          ...(getPortalTenantN8nExtras(customerStatus, customerEmail)),
        }),
      });
      let data: Record<string, unknown> = {};
      try {
        data = (await res.json()) as Record<string, unknown>;
      } catch {
        /* ignore */
      }
      const docUrl = typeof data.document_url === 'string' ? data.document_url.trim() : '';
      if (!res.ok || !docUrl) {
        const errStr =
          typeof data.error === 'string'
            ? data.error
            : !res.ok
              ? `Der Generator antwortete mit Fehler ${res.status}.`
              : 'Antwort ohne document_url — der Generator-Workflow ist evtl. noch nicht aktiv.';
        throw new Error(errStr);
      }
      await onDocumentReady(data, res);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (pickerOpen || !template || !schema) {
    return (
      <div className={`${VERTRAG_SHELL} animate-fade-in`} data-testid="portal-vertrag">
        <Header />
        <div className="min-h-0 flex-1 px-3 py-3">
          <TypePicker
            drafts={drafts}
            onPick={applyTemplate}
            onLoad={handleLoadDraft}
          />
        </div>
      </div>
    );
  }

  const extraVisible = (template.extraSlots || []).filter((s) =>
    isBlockActive(template, enabledBlocks, s.blockId),
  );
  const geruestPreview =
    template.maturity === 'geruest'
      ? buildGeruestPreview(template, form, enabledBlocks, extraValues, eigeneKlauseln)
      : '';
  const isGeruest = template.maturity === 'geruest';

  return (
    <div className={`${VERTRAG_SHELL} animate-fade-in`} data-testid="portal-vertrag">
      <Header
        subtitle={template.label}
        action={
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-violet-400/40 bg-[#07070a] px-2 py-1 text-[11px] text-violet-100 hover:bg-violet-600/30"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Typ wählen
          </button>
        }
      />

      <form onSubmit={handleSubmit} className="min-h-0 flex-1 space-y-5 px-3 py-3">
        <p className="text-slate-400 text-sm">{template.blurb}</p>
        {isGeruest && (
          <p className="text-amber-400/90 text-xs mt-2" data-testid="contract-muster-banner">
            {template.sourceMuster
              ? `${template.sourceMuster.label} — Klauseln an/aus, Platzhalter wie Mietvertrag. Kein ARKORBIT-AGB. Datei auf dem Server nur beim Mietvertrag.`
              : 'Gerüst — leere Slots, keine erfundenen Volltexte.'}
          </p>
        )}

        <div
          data-testid="contract-layout"
          className={isGeruest ? 'mt-5 lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start' : 'mt-5 space-y-5'}
        >
        <div className="space-y-5 min-w-0">
        <InhalteList
          template={template}
          form={form}
          extraValues={extraValues}
          enabledBlocks={enabledBlocks}
          individuelle={eigeneKlauseln}
          onToggle={toggleBlock}
          onAddIndividuell={() =>
            setEigeneKlauseln((prev) =>
              prev.length >= MAX_INDIVIDUELL_BLOCKS ? prev : [...prev, newIndividuellBlock()],
            )
          }
          onPatchIndividuell={(id, patch) =>
            setEigeneKlauseln((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)))
          }
          onRemoveIndividuell={(id) => setEigeneKlauseln((prev) => prev.filter((x) => x.id !== id))}
        />

        {Object.entries(schema.properties).map(([key, prop]) => {
          if (template.id === 'gesellschaftervertrag' && isGesellschafterSchemaKey(key)) {
            if (key !== 'gesellschafter_a') return null;
            return (
              <RepeatableGesellschafterList
                key="gesellschafter-n"
                form={form}
                extraValues={extraValues}
                onReplace={(liste) => {
                  setExtraValues((prev) => ({ ...prev, [GESELLSCHAFTER_LIST_KEY]: liste }));
                  setForm((prev) => syncGesellschafterForm(prev, liste));
                }}
              />
            );
          }
          if (prop.type === 'object' && prop.properties) {
            return (
              <fieldset key={key} className="border border-violet-400/30 rounded-lg p-4 space-y-3">
                <legend className="px-2 text-sm font-semibold text-violet-200">{prop.title || key}</legend>
                {prop.description && <p className="text-slate-500 text-xs -mt-1">{prop.description}</p>}
                {Object.entries(prop.properties).map(([subKey, subProp]) => (
                  <FieldControl
                    key={`${key}.${subKey}`}
                    path={`${key}.${subKey}`}
                    prop={subProp}
                    value={getByPath(form, `${key}.${subKey}`)}
                    required={isPathRequired(schema, form, `${key}.${subKey}`)}
                    error={errors[`${key}.${subKey}`]}
                    onChange={update}
                    clauseLabels={template.clauseLabels}
                  />
                ))}
              </fieldset>
            );
          }
          const leaf = leafByPath[key];
          if (leaf?.conditional && !isConditionalActive(leaf, form)) return null;
          const hideBooleanBecauseBlock =
            prop.type === 'boolean' && template.blocks.some((b) => b.bindsBoolean === key);
          if (hideBooleanBecauseBlock) return null;
          return (
            <FieldControl
              key={key}
              path={key}
              prop={prop}
              value={getByPath(form, key)}
              required={isPathRequired(schema, form, key)}
              error={errors[key]}
              onChange={update}
              clauseLabels={template.clauseLabels}
            />
          );
        })}

        {extraVisible.length > 0 && (
          <fieldset className="border border-violet-400/30 rounded-lg p-4 space-y-3">
            <legend className="px-2 text-sm font-semibold text-violet-200">Genauere Optionen (lokal)</legend>
            <p className="text-slate-500 text-xs">
              Bleiben in der Vorlage. Der Server-Mietvertrag sendet sie nicht mit.
            </p>
            {extraVisible.map((slot) => (
              <ExtraSlotControl
                key={slot.id}
                label={slot.label}
                kind={slot.kind}
                enumValues={slot.enum}
                placeholder={slot.placeholder}
                value={extraValues[slot.id]}
                onChange={(v) => setExtraValues((prev) => ({ ...prev, [slot.id]: v }))}
              />
            ))}
          </fieldset>
        )}

        <div className={`${VERTRAG_EDGE} p-4 space-y-3`}>
          <p className="text-sm font-bold text-white">Vorlage speichern / laden</p>
          <p className="text-slate-500 text-xs">
            Vorlage nur in diesem Browser und diesem Konto gespeichert — nicht kanzleiweit.
          </p>
          <input
            className={FIELD_BASE}
            placeholder="Vorlagenname (z. B. Standard Wohnung Demo)"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSaveDraft}
              className="px-3 py-1.5 rounded-md border border-violet-400/40 bg-[#07070a] text-violet-100 text-sm flex items-center gap-1.5 hover:bg-violet-600/30"
            >
              <Save className="w-3.5 h-3.5" /> Speichern
            </button>
            <select
              className={`${FIELD_BASE} max-w-xs`}
              value={loadId}
              onChange={(e) => {
                const v = e.target.value;
                setLoadId(v);
                if (v) handleLoadDraft(v);
              }}
            >
              <option value="">Gespeicherte Vorlage laden…</option>
              {drafts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.templateId})
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!loadId}
              onClick={handleDeleteDraft}
              className="px-3 py-1.5 rounded-md border border-violet-400/40 bg-[#07070a] hover:bg-violet-600/30 disabled:opacity-40 text-violet-100 text-sm flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Löschen
            </button>
          </div>
          {draftNotice && <p className="text-emerald-400/90 text-xs">{draftNotice}</p>}
        </div>

        {serverError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-2 text-red-300 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{serverError}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className={`w-full font-semibold py-2.5 rounded-md flex items-center justify-center gap-2 ${busy ? PORTAL_BTN.primaryDisabled : PORTAL_BTN.primary}`}
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Entwurf wird erstellt…
            </>
          ) : template.maturity === 'engine' ? (
            <>
              <FilePlus2 className="w-4 h-4" /> Vertrags-Entwurf erstellen
            </>
          ) : (
            <>
              <FilePlus2 className="w-4 h-4" /> Entwurf kopieren
            </>
          )}
        </button>

        <div className={`${VERTRAG_EDGE} p-4 flex items-start gap-3`}>
          <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-slate-500 text-xs leading-relaxed">
            Entwurfs-Assistent, <strong className="text-amber-400/90">keine Rechtsberatung</strong> (§ 9 RAO).
            Keine erfundenen Kanzlei-Muster. Nur fiktive Daten verwenden.
          </p>
        </div>
        </div>

        {isGeruest && (
          <aside
            data-testid="contract-geruest-pane"
            className="mt-5 lg:mt-0 min-w-0 lg:sticky lg:top-3 lg:max-h-[calc(100dvh-5.5rem)] lg:overflow-y-auto lg:self-start"
          >
            <fieldset className={`${VERTRAG_EDGE} p-4 space-y-2`}>
              <legend className="px-2 text-sm font-semibold text-violet-200">Fließtext-Entwurf</legend>
              <p className="text-slate-500 text-xs">
                Inhalte an/aus und Felder füllen. Leere Werte erscheinen als {EMPTY_SLOT}. Es wird keine Datei auf dem Server erzeugt.
              </p>
              <pre
                data-testid="contract-geruest-preview"
                className="whitespace-pre-wrap text-xs text-slate-200 bg-[#07070a] border border-violet-400/30 rounded-lg p-3 lg:max-h-[calc(100dvh-10rem)] max-h-[28rem] overflow-y-auto font-sans leading-relaxed"
              >
                {geruestPreview}
              </pre>
            </fieldset>
          </aside>
        )}
        </div>
      </form>

      <div className="flex items-center gap-2 text-slate-600 text-xs">
        <FileText className="w-3.5 h-3.5" />
        <span>
          {template.maturity === 'engine'
            ? 'Mietvertrag: Verarbeitung auf dem ARKORBIT-Server (EU), Ausgabe in Ihren Dokumenten.'
            : `${template.sourceMuster ? template.sourceMuster.label : 'Gerüst'} bleibt lokal. Es wird keine Datei auf dem Server erzeugt, kein ARKORBIT-AGB.`}
        </span>
      </div>
    </div>
  );
};

function InhalteList({
  template,
  form,
  extraValues,
  enabledBlocks,
  individuelle,
  onToggle,
  onAddIndividuell,
  onPatchIndividuell,
  onRemoveIndividuell,
}: {
  template: ContractTemplateDef;
  form: ContractValues;
  extraValues: Record<string, unknown>;
  enabledBlocks: string[];
  individuelle: EigeneKlausel[];
  onToggle: (id: string) => void;
  onAddIndividuell: () => void;
  onPatchIndividuell: (id: string, patch: { titel?: string; text?: string }) => void;
  onRemoveIndividuell: (id: string) => void;
}) {
  const [hoverId, setHoverId] = useState<string | null>(null);
  return (
    <div data-testid="contract-inhalte">
      <p className="text-sm font-bold text-white mb-2">Inhalte des Vertrags</p>
      <p className="text-slate-500 text-xs mb-2">
        Eine Zeile pro Inhalt. Klick schaltet an oder aus. Maus darüber zeigt den möglichen Vertragstext
        (Entwurf, kein ARKORBIT-AGB). Plus <strong className="text-slate-400">Individuell</strong> für
        eigene Überschrift und Wortlaut — der Text steht im Fließtext-Entwurf. Kein ARKORBIT-Muster.
        Entwurf, keine Rechtsberatung (§ 9 RAO).
      </p>
      <ul className="flex flex-col gap-1">
        {template.blocks.map((b) => {
          const on = isBlockActive(template, enabledBlocks, b.id);
          const tone = inhalteRowTone(Boolean(b.locked), on);
          const clauseText =
            template.maturity === 'geruest'
              ? buildGeruestBlockPreview(template, form, extraValues, b.id)
              : '';
          const preview = (clauseText || b.hint || '').trim();
          const showPreview = hoverId === b.id && Boolean(preview);
          return (
            <li
              key={b.id}
              className="relative"
              onMouseEnter={() => setHoverId((cur) => nextInhalteHoverId(cur, b.id, 'enter'))}
              onMouseLeave={() => setHoverId((cur) => nextInhalteHoverId(cur, b.id, 'leave'))}
            >
              <button
                type="button"
                disabled={b.locked}
                data-testid={`contract-block-${b.id}`}
                data-inhalte-tone={tone}
                aria-pressed={on}
                onFocus={() => setHoverId((cur) => nextInhalteHoverId(cur, b.id, 'enter'))}
                onBlur={() => setHoverId((cur) => nextInhalteHoverId(cur, b.id, 'leave'))}
                onClick={() => onToggle(b.id)}
                className={`w-full text-left text-sm px-3 py-2 rounded-lg border flex items-center justify-between gap-3 transition-colors ${
                  INHALTE_ROW_TONE_CLASS[tone]
                } ${b.locked ? 'opacity-80 cursor-default' : 'cursor-pointer'}`}
              >
                <span className="min-w-0 truncate">{b.label}</span>
                <span className="shrink-0 text-[11px] uppercase tracking-wide text-slate-500">
                  {b.locked ? 'fix' : on ? 'an' : 'aus'}
                </span>
              </button>
              {showPreview ? (
                <div
                  role="tooltip"
                  data-testid={`contract-block-preview-${b.id}`}
                  className="pointer-events-none absolute z-30 left-0 right-0 top-full mt-1 lg:left-full lg:right-auto lg:top-0 lg:ml-2 lg:w-80 xl:w-96"
                >
                  <div className="rounded-lg border border-violet-400/30 bg-[#07070a] p-3">
                    <p className="text-[10px] text-slate-500 mb-1">
                      Mögliche Formulierung im Entwurf — kein ARKORBIT-AGB.
                    </p>
                    <pre className="whitespace-pre-wrap text-xs text-slate-200 font-sans leading-relaxed max-h-64 overflow-y-auto">
                      {preview}
                    </pre>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
        {individuelle.map((k, idx) => (
          <li key={k.id} data-testid={`contract-individuell-row-${idx}`}>
            <div
              data-testid={`contract-individuell-editor-${idx}`}
              className="space-y-2 border border-violet-400/30 bg-[#07070a] rounded-lg p-3"
            >
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">Überschrift</span>
                <input
                  className={`${FIELD_BASE} mt-0.5`}
                  aria-label={`Überschrift ${idx + 1}`}
                  placeholder={`Überschrift ${idx + 1}`}
                  value={k.titel}
                  onChange={(e) => onPatchIndividuell(k.id, { titel: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">Wortlaut</span>
                <textarea
                  className={`${FIELD_BASE} mt-0.5 min-h-[4.5rem]`}
                  aria-label={`Wortlaut ${idx + 1}`}
                  placeholder="Eigener Wortlaut — nur was hier steht, landet im Fließtext-Entwurf."
                  value={k.text}
                  onChange={(e) => onPatchIndividuell(k.id, { text: e.target.value })}
                />
              </label>
              <button
                type="button"
                data-testid={`contract-individuell-remove-${idx}`}
                className="text-xs text-slate-500 hover:text-red-300"
                onClick={() => onRemoveIndividuell(k.id)}
              >
                Block entfernen
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        data-testid="contract-individuell-add"
        onClick={onAddIndividuell}
        className="mt-2 text-sm text-violet-200 hover:text-violet-100 flex items-center gap-1"
      >
        <Plus className="w-3.5 h-3.5" /> Individuell
      </button>
    </div>
  );
}

function RepeatableGesellschafterList({
  form,
  extraValues,
  onReplace,
}: {
  form: ContractValues;
  extraValues: Record<string, unknown>;
  onReplace: (liste: GesellschafterListeEintrag[]) => void;
}) {
  const liste = resolveGesellschafterListe(form, extraValues);

  const patch = (index: number, patchFields: Partial<GesellschafterListeEintrag>) => {
    const next = liste.map((e, i) => (i === index ? { ...e, ...patchFields } : e));
    onReplace(next);
  };

  return (
    <div className="space-y-3" data-testid="contract-gesellschafter-list">
      <p className="text-sm font-bold text-white">Gesellschafter</p>
      <p className="text-slate-500 text-xs">
        Start A und B. Plus fügt C, D, E … hinzu — so viele die Gesellschaft hat. Fiktive Testdaten, kein
        Mandanten-PII.
      </p>
      {liste.map((e, i) => {
        const letter = gesellschafterLetter(i);
        return (
          <fieldset
            key={`g-${i}`}
            data-testid={`contract-party-gesellschafter-${letter}`}
            className="border border-violet-400/30 rounded-lg p-4 space-y-3"
          >
            <legend className="px-2 text-sm font-semibold text-violet-200">Gesellschafter {letter}</legend>
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Name / Firma</label>
              <input
                className={FIELD_BASE}
                placeholder="Name / Firma"
                value={e.name}
                onChange={(ev) => patch(i, { name: ev.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Anschrift</label>
              <input
                className={FIELD_BASE}
                placeholder="Anschrift"
                value={e.anschrift}
                onChange={(ev) => patch(i, { anschrift: ev.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Anteil (%)</label>
              <input
                className={FIELD_BASE}
                inputMode="decimal"
                placeholder="z. B. 50"
                value={e.anteil_prozent}
                onChange={(ev) => patch(i, { anteil_prozent: ev.target.value })}
              />
            </div>
            {liste.length > GESELLSCHAFTER_MIN_COUNT ? (
              <button
                type="button"
                data-testid={`contract-party-remove-${letter}`}
                className="text-xs text-slate-500 hover:text-red-300"
                onClick={() => onReplace(liste.filter((_, j) => j !== i))}
              >
                Gesellschafter {letter} entfernen
              </button>
            ) : (
              <p className="text-slate-500 text-xs">
                Nur ein Gesellschafter — Alleingesellschafter möglich. Plus fügt B, C, … hinzu.
              </p>
            )}
          </fieldset>
        );
      })}
      <button
        type="button"
        data-testid="contract-party-add"
        onClick={() => onReplace([...liste, emptyGesellschafterEintrag()])}
        className="text-sm text-violet-200 hover:text-violet-100 flex items-center gap-1"
      >
        <Plus className="w-3.5 h-3.5" /> Gesellschafter
      </button>
    </div>
  );
}

const Header: React.FC<{ subtitle?: string; action?: React.ReactNode }> = ({ subtitle, action }) => (
  <header className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-violet-400/30 px-3.5 py-3">
    <div className="min-w-0">
      <h1 className="m-0 flex items-center gap-2 text-sm font-semibold text-white">
        <FileText className="h-4 w-4 text-violet-300" aria-hidden />
        Vertrag erstellen
      </h1>
      <p className="m-0 mt-0.5 text-[11px] leading-relaxed text-violet-200/80">
        {subtitle || 'Vorlagen · Inhalte · Keine Rechtsberatung (§ 9 RAO)'}
      </p>
      <p className="m-0 mt-0.5 text-[11px] leading-relaxed text-slate-500">
        Typ wählen, Felder anpassen, als wiederverwendbare Vorlage speichern. Keine erfundenen Volltexte.
      </p>
    </div>
    {action}
  </header>
);

const TypePicker: React.FC<{
  drafts: SavedContractDraft[];
  onPick: (tpl: ContractTemplateDef) => void;
  onLoad: (id: string) => void;
}> = ({ drafts, onPick, onLoad }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {CONTRACT_TEMPLATES.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onPick(t)}
          data-testid={`contract-type-${t.id}`}
          className={`text-left p-4 transition-colors hover:border-violet-400/60 ${VERTRAG_EDGE}`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-white font-bold text-sm">{t.label}</span>
            <span
              className={`text-[10px] tracking-wide px-2 py-0.5 rounded-full ${
                t.maturity === 'engine'
                  ? 'uppercase border border-violet-400/40 bg-[#07070a] text-violet-200'
                  : t.sourceMuster
                    ? 'bg-amber-500/15 text-amber-300'
                    : 'uppercase border border-violet-400/20 bg-[#07070a] text-slate-400'
              }`}
            >
              {t.maturity === 'engine' ? 'Engine' : t.sourceMuster ? PHILLIP_MUSTER_LABEL : 'Gerüst'}
            </span>
          </div>
          <p className="text-slate-500 text-xs mt-1">{t.blurb}</p>
        </button>
      ))}
    </div>
    {drafts.length > 0 && (
      <div className={`${VERTRAG_EDGE} p-4`}>
        <p className="text-sm font-bold text-white mb-2">Gespeicherte Vorlagen</p>
        <ul className="space-y-1">
          {drafts.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => onLoad(d.id)}
                className="text-sm text-violet-200 hover:text-violet-100"
              >
                {d.name}
                <span className="text-slate-500"> · {d.templateId}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

function ExtraSlotControl({
  label,
  kind,
  enumValues,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  kind: 'boolean' | 'enum' | 'string';
  enumValues?: string[];
  placeholder?: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (kind === 'boolean') {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="accent-violet-400 w-4 h-4"
        />
        <span className="text-sm text-slate-200">{label}</span>
      </label>
    );
  }
  if (kind === 'enum' && enumValues?.length) {
    return (
      <div>
        <label className="block text-sm font-medium text-slate-200 mb-1">{label}</label>
        <select
          className={FIELD_BASE}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        >
          {enumValues.map((opt) => (
            <option key={opt} value={opt === '—' ? '' : opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <div>
      <label className="block text-sm font-medium text-slate-200 mb-1">{label}</label>
      <textarea
        className={`${FIELD_BASE} min-h-[3.5rem]`}
        placeholder={placeholder || ''}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function isPathRequired(schema: ContractSchema, form: ContractValues, path: string): boolean {
  const leaf = collectLeafFields(schema).find((f) => f.path === path);
  return leaf ? isEffectivelyRequired(leaf, form) : false;
}

function buildDisplayName(templateLabel: string, form: ContractValues): string {
  const mieter = getByPath(form, 'mieter.name');
  const who = typeof mieter === 'string' && mieter.trim() ? ` — ${mieter.trim()}` : '';
  const stamp = new Date().toISOString().slice(0, 10);
  return `${templateLabel}${who} (${stamp})`;
}

interface FieldControlProps {
  path: string;
  prop: SchemaProp;
  value: unknown;
  required: boolean;
  error?: string;
  onChange: (path: string, value: unknown) => void;
  clauseLabels?: Record<string, string>;
}

const FieldControl: React.FC<FieldControlProps> = ({
  path,
  prop,
  value,
  required,
  error,
  onChange,
  clauseLabels,
}) => {
  const label = (
    <label className="block text-sm font-medium text-slate-200 mb-1">
      {prop.title || path}
      {required && <span className="text-red-400 ml-1">*</span>}
    </label>
  );

  if (prop.type === 'boolean') {
    return (
      <div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(path, e.target.checked)}
            className="accent-violet-400 w-4 h-4"
          />
          <span className="text-sm text-slate-200">
            {prop.title || path}
            {required && <span className="text-red-400 ml-1">*</span>}
          </span>
        </label>
        {prop.description && <p className="text-slate-500 text-xs mt-1">{prop.description}</p>}
      </div>
    );
  }

  if (prop.type === 'array' && prop.items?.enum) {
    const selected: string[] = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (opt: string) => {
      const next = selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt];
      onChange(path, next);
    };
    return (
      <div>
        {label}
        {prop.description && <p className="text-slate-500 text-xs mb-2 -mt-0.5">{prop.description}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {prop.items.enum.map((opt) => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                className="accent-violet-400 w-4 h-4"
              />
              {clauseLabels?.[opt] || opt}
            </label>
          ))}
        </div>
        {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
      </div>
    );
  }

  if (prop.enum && prop.enum.length) {
    return (
      <div>
        {label}
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(path, e.target.value)}
          className={`${FIELD_BASE} ${error ? 'border-red-500/60' : ''}`}
        >
          <option value="">Bitte wählen…</option>
          {prop.enum.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {prop.description && !error && <p className="text-slate-500 text-xs mt-1">{prop.description}</p>}
        {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
      </div>
    );
  }

  const inputType = prop.format === 'date' ? 'date' : 'text';
  const asTextarea = prop.format === 'textarea' || (prop.type === 'string' && (prop.maxLength || 0) >= 400);
  if (asTextarea && prop.format !== 'date') {
    return (
      <div>
        {label}
        <textarea
          value={typeof value === 'string' ? value : value == null ? '' : String(value)}
          onChange={(e) => onChange(path, e.target.value)}
          placeholder="Leerer Slot — kein vorformulierter ARKORBIT-Text."
          className={`${FIELD_BASE} min-h-[4.5rem] ${error ? 'border-red-500/60' : ''}`}
        />
        {prop.description && !error && <p className="text-slate-500 text-xs mt-1">{prop.description}</p>}
        {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
      </div>
    );
  }
  return (
    <div>
      {label}
      <input
        type={inputType}
        inputMode={prop.type === 'number' ? 'decimal' : undefined}
        value={typeof value === 'string' ? value : value == null ? '' : String(value)}
        onChange={(e) => onChange(path, e.target.value)}
        placeholder={prop.type === 'number' ? 'z. B. 1250' : ''}
        className={`${FIELD_BASE} ${error ? 'border-red-500/60' : ''}`}
      />
      {prop.description && !error && <p className="text-slate-500 text-xs mt-1">{prop.description}</p>}
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default ContractGeneratorView;
